import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { ArrowLeft, Plus, ExternalLink } from "lucide-react";

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", app_type: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get(`/projects`, { params: { client_id: id } }),
    ]);
    setClient(c.data);
    setProjects(p.data);
    // Auto-open new project if no projects yet
    if (p.data.length === 0) setShowNew(true);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const createProject = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        app_type: form.app_type.trim() || null,
      };
      const { data } = await api.post(`/clients/${id}/projects`, payload);
      setForm({ name: "", app_type: "" });
      setShowNew(false);
      navigate(`/admin/projects/${data.id}`);
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this client? They'll be hidden from the main list.")) return;
    await api.delete(`/clients/${id}`);
    navigate("/admin/clients");
  };

  if (!client) return <p className="text-[#9c95a8]">Loading…</p>;

  return (
    <div data-testid="client-detail-page">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-sm text-[#bdb6c4] hover:text-white mb-6">
        <ArrowLeft size={14} /> Back to clients
      </Link>

      <div className="bg-[#13121f] border border-white/10 rounded-2xl p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-bold tracking-tight">{client.name}</h1>
            <div className="text-[#bdb6c4] mt-2 text-sm space-x-3">
              {client.company && <span>{client.company}</span>}
              {client.email && <a href={`mailto:${client.email}`} className="hover:text-[#f5c76e]">{client.email}</a>}
              {client.phone && <span>{client.phone}</span>}
            </div>
            <p className="font-mono text-[10px] text-[#6b6677] mt-3">
              Client since {new Date(client.created_at).toLocaleDateString()}
              {client.from_lead_id && " · converted from a postcard ✦"}
            </p>
          </div>
          <button
            onClick={archive}
            className="px-4 py-2 rounded-full border border-white/10 text-sm text-[#bdb6c4] hover:bg-white/[0.04] transition-colors"
          >
            Archive
          </button>
        </div>
        {client.notes && (
          <div className="mt-5 bg-[#0a0a14] border border-white/5 rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6b6677] mb-2">Notes</p>
            <p className="text-[#f4ede1] whitespace-pre-wrap leading-relaxed">{client.notes}</p>
          </div>
        )}
      </div>

      <div className="mt-8 flex items-end justify-between flex-wrap gap-4">
        <h2 className="font-display text-2xl font-bold">Projects</h2>
        <button
          onClick={() => setShowNew((v) => !v)}
          data-testid="new-project-button"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f5c76e] text-[#0a0a14] font-semibold text-sm hover:bg-[#ffd485] transition-colors"
        >
          <Plus size={14} /> New project
        </button>
      </div>

      {showNew && (
        <form onSubmit={createProject} className="mt-4 bg-[#13121f] border border-white/10 rounded-2xl p-5 space-y-3" data-testid="new-project-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9c95a8] block mb-2">Project name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="project-name-input"
                placeholder="Acme — Booking app"
                className="w-full bg-[#0a0a14] border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-[#f5c76e]/60 transition-colors"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9c95a8] block mb-2">App type</label>
              <input
                value={form.app_type}
                onChange={(e) => setForm({ ...form, app_type: e.target.value })}
                data-testid="project-type-input"
                placeholder="iOS · Web · SaaS · …"
                className="w-full bg-[#0a0a14] border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-[#f5c76e]/60 transition-colors"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={busy || !form.name.trim()}
            data-testid="create-project-button"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#f5c76e] text-[#0a0a14] font-semibold text-sm hover:bg-[#ffd485] transition-colors disabled:opacity-60"
          >
            {busy ? "Creating…" : "Create project →"}
          </button>
        </form>
      )}

      <div className="mt-4 space-y-2" data-testid="projects-list">
        {projects.length === 0 && !showNew ? (
          <p className="text-[#9c95a8] text-sm">No projects yet — create one above to start tracking.</p>
        ) : (
          projects.map((p) => {
            const totalTasks = (p.stages || []).reduce((a, s) => a + (s.tasks?.length || 0), 0);
            const doneTasks = (p.stages || []).reduce((a, s) => a + (s.tasks?.filter((t) => t.done).length || 0), 0);
            const activeStage = (p.stages || []).find((s) => s.status === "active")?.name || "—";
            return (
              <Link
                key={p.id}
                to={`/admin/projects/${p.id}`}
                className="block bg-[#13121f] border border-white/10 rounded-xl px-5 py-4 hover:border-[#f5c76e]/30 transition-colors"
              >
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{p.name}</p>
                      {p.app_type && <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6b6677]">{p.app_type}</span>}
                    </div>
                    <p className="text-sm text-[#bdb6c4] mt-1">
                      Now: <span className="text-[#f5c76e]">{activeStage}</span> · {doneTasks}/{totalTasks} tasks done
                    </p>
                  </div>
                  <ExternalLink size={14} className="text-[#6b6677]" />
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ClientDetail;
